import moment from "moment";

import type IntlProviderService from "@app/services/intl-provider";
import type { TranslatorService } from "@app/services/translator";

import { removeTimeOffset } from "./time";
import { SHORT_DATE_FORMAT } from "../utils/dates";

export const isToday = (timezone: string, date: string): boolean =>
  moment().tz(timezone).format(SHORT_DATE_FORMAT) === date;

export const isTomorrow = (timezone: string, date: string): boolean =>
  moment().tz(timezone).add(1, "day").format(SHORT_DATE_FORMAT) === date;

export const formatDate = (date: moment.Moment): string =>
  date.format(SHORT_DATE_FORMAT);

export const getTranslatedRelativeDay = (
  date: string,
  timezone: string,
  intlProvider: IntlProviderService,
  translator: TranslatorService
): string | null =>
  isToday(timezone, date)
    ? translator.t("!route.price_calendar.today")
    : isTomorrow(timezone, date)
      ? translator.t("!route.price_calendar.tomorrow")
      : intlProvider.formatDate(date, {
          weekday: "short"
        });

export const getDayDifference = (
  departure_datetime: string,
  arrival_datetime: string
): number => {
  const departure_without_offset = removeTimeOffset(departure_datetime);
  const arrival_without_offset = removeTimeOffset(arrival_datetime);

  const departure_date = moment(departure_without_offset).startOf("day");
  const arrival_date = moment(arrival_without_offset).startOf("day");

  return arrival_date.diff(departure_date, "days");
};

// @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
const parseDate = (datetime: string): string => datetime.split("T")[0];

export const isBetweenTimes = (
  datetime_iso: string,
  period_start_datetime_iso: string,
  period_end_datetime_iso: string
) => {
  return moment(datetime_iso).isBetween(
    moment(period_start_datetime_iso),
    moment(period_end_datetime_iso)
  );
};

export const isBetweenDates = (
  datetime: string,
  period_start_datetime: string,
  period_end_datetime: string | null = null
): boolean => {
  const ref_date = parseDate(datetime);
  const start_date = parseDate(period_start_datetime);
  const end_date = period_end_datetime ? parseDate(period_end_datetime) : null;

  const is_start_date_before_ref = start_date <= ref_date;
  const is_end_date_after_ref = end_date === null ? true : end_date >= ref_date;

  return is_start_date_before_ref && is_end_date_after_ref;
};

/**
 * Retrieves a list of time zone names that match a given offset.
 *
 * @param {string} offset - The time zone offset in the format "+HH:mm" or "-HH:mm".
 * @returns {string[]} An array of time zone names that match the given offset.
 */
export const getZoneFromOffset = (offset: string): string[] =>
  moment.tz.names().filter(tz => moment.tz(tz).format("Z") === offset);
